import * as React from 'react';
import {IPlayerSubtitle} from '@pluto-tv/assemble-player';
import {IDateRange, isValEmpty, useCancellableSummon} from '@pluto-tv/assemble';
import {cloneDeep, isBoolean, throttle} from 'lodash-es';
import {DateTime as luxon} from 'luxon';

import {IClip} from 'models/clips';
import {IActiveRegion} from 'models/activeRegions';
import {IElasticSearchRes, IListPayload, IListQuery} from 'models/generic';
import {ITerritory} from 'models/territory';

import {HmsToSeconds} from 'helpers/secondsToHms';
import {useUserRegions} from 'helpers/useUserRegions';

const HIGHEST_INT32 = 2147483647;

interface IClipSearch
  extends Partial<
      Omit<
        IClip,
        | 'author'
        | 'isUsed'
        | 'promotional'
        | 'liveBroadcast'
        | 'published'
        | 'activeRegion'
        | 'duration'
        | 'regionFilter'
        | 'distributeAs'
        | 'url'
      >
    >,
    IListQuery {
  // Author needs to be simplified from an object to a string
  author?: string;
  // Certain fields are expected to be a string representation of a boolean in the API
  isUsed?: string;
  promotional?: string;
  liveBroadcast?: string;
  published?: string;
  // Active Region needs to be an array
  activeRegion?: string[];
  // regionFilter -> territories
  territories?: string[];
  // Uploaded At
  timeWindow?: IDurationWindow;
  // Duration
  duration?: IDurationWindow;
  // distributeAs.AVOD -> distributeAsAVOD
  distributeAsAVOD?: string;
  // URL needs to be an array
  url?: string[];
}

interface IDurationWindow {
  from: string | number;
  to: string | number;
}

export interface IClipDuration {
  start?: string;
  end?: string;
}

export interface IClipMetaSearch {
  uploadDate?: IDateRange;
  duration?: IClipDuration;
  window?: 'day' | 'week' | 'month';
}

interface IUseClipSearch {
  clips?: IListPayload<IClip>;
  isError: boolean;
  isFetching: boolean;
  rowsPerPage: 25 | 50 | 75 | 100;
  setRowsPerPage;
  page: number;
  setPage;
  sort: string;
  setSort;
  clearSearch: (doSearch?: boolean) => void;
  search: (searchModel?: Partial<IClip>, searchMeta?: IClipMetaSearch, sort?: string) => void;
}

const transformReq = (
  model: Partial<IClip>,
  page: number,
  perPage: 25 | 50 | 75 | 100,
  sort: string,
  searchMeta?: IClipMetaSearch,
  activeRegions?: IActiveRegion[],
  territories?: {[id: string]: ITerritory[]},
): IClipSearch => {
  const searchObj = cloneDeep(model) as IClipSearch;

  if (model.author?.name) {
    delete searchObj.author;

    searchObj.author = model.author.name;
  }

  if (model.activeRegion) {
    searchObj.activeRegion = [model.activeRegion];
  } else {
    searchObj.activeRegion = activeRegions?.map(ar => ar.code.toLowerCase());
  }

  if (model.url) {
    searchObj.url = [model.url];
  }

  if (isBoolean(model.isUsed)) {
    searchObj.isUsed = model.isUsed.toString();
  }
  if (isBoolean(model.promotional)) {
    searchObj.promotional = model.promotional.toString();
  }
  if (isBoolean(model.liveBroadcast)) {
    searchObj.liveBroadcast = model.liveBroadcast.toString();
  }
  if (isBoolean(model.published)) {
    searchObj.published = model.published.toString();
  }

  if (!isValEmpty(model.distributeAs?.AVOD)) {
    searchObj.distributeAsAVOD = model.distributeAs?.AVOD.toString();
    delete (searchObj as any).distributeAs;
  }

  if (model.regionFilter) {
    searchObj.territories = [...model.regionFilter];
    delete (searchObj as any).regionFilter;
  } else {
    searchObj.territories = [];

    Object.keys(territories || {}).forEach(t => {
      (territories || {})[t].forEach(ter => {
        searchObj.territories?.push(ter.id.toLowerCase());
      });
    });
  }

  if (searchMeta?.window || searchMeta?.uploadDate) {
    searchObj.timeWindow = {
      from: '',
      to: '',
    };

    const now = new Date();

    if (searchMeta.window) {
      switch (searchMeta.window) {
        case 'day':
          searchObj.timeWindow.from = luxon.fromJSDate(now).minus({days: 1}).toISO();
          searchObj.timeWindow.to = now.toISOString();
          break;
        case 'week':
          searchObj.timeWindow.from = luxon.fromJSDate(now).minus({weeks: 1}).toISO();
          searchObj.timeWindow.to = now.toISOString();
          break;
        case 'month':
          searchObj.timeWindow.from = luxon.fromJSDate(now).minus({months: 1}).toISO();
          searchObj.timeWindow.to = now.toISOString();
          break;
      }
    } else if (searchMeta.uploadDate) {
      searchObj.timeWindow.from = luxon
        .fromJSDate(new Date(searchMeta.uploadDate.start), {zone: 'America/Los_Angeles'})
        .startOf('day')
        .toISO();
      searchObj.timeWindow.to = luxon
        .fromJSDate(new Date(searchMeta.uploadDate.end!), {zone: 'America/Los_Angeles'})
        .endOf('day')
        .toISO();
    }
  }

  if (searchMeta?.duration) {
    let from = HmsToSeconds(searchMeta.duration?.start as string);
    let to = HmsToSeconds(searchMeta.duration?.end as string);

    // If `to` is invalid, make it a high number
    if (to === 0) {
      to = HIGHEST_INT32;
    }

    // Inverse the durations if they are in the wrong order
    if (from > to) {
      const to2 = from;
      from = to;
      to = to2;
    }

    searchObj.duration = {
      from,
      to,
    };
  }

  return {
    ...searchObj,
    sort,
    limit: perPage,
    offset: page * perPage,
  };
};

const defaultSearch: Partial<IClip> = {
  provider: 'jwplatform',
};

const defaultSort = 'createdAt:desc';

export const useClipSearch = (): IUseClipSearch => {
  const [isFetching, setIsFetching] = React.useState(true);
  const [isError, setIsError] = React.useState(false);

  const [clips, setClips] = React.useState<IListPayload<IClip>>();

  const [rowsPerPage, setRowsPerPage] = React.useState<25 | 50 | 75 | 100>(25);
  const [page, setPage] = React.useState<number>(0);
  const [sort, setSort] = React.useState<string>(defaultSort);

  const {activeRegions, territoriesByRegion} = useUserRegions();
  const [summon] = useCancellableSummon();

  const clearSearch = async (doSearch = false): Promise<void> => {
    setPage(0);
    setSort(defaultSort);

    if (doSearch) {
      await search({}, {}, defaultSort);
    }
  };

  const search = throttle(
    async (searchModel?: Partial<IClip>, searchMeta?: IClipMetaSearch, customSort?: string) => {
      setIsFetching(true);

      const mySort = customSort ? customSort : sort;

      let cleanedModel: Partial<IClip> = {};

      if (!searchModel || isValEmpty(searchModel)) {
        cleanedModel = cloneDeep(defaultSearch);
      } else {
        Object.keys(searchModel).forEach(key => {
          const fieldName: keyof IClip = key as keyof IClip;

          if (!isValEmpty(searchModel[fieldName])) {
            cleanedModel[fieldName] = cloneDeep(searchModel[fieldName]) as any;
          }
        });
      }

      const fullReq = transformReq(
        cleanedModel,
        page,
        rowsPerPage,
        mySort,
        searchMeta,
        activeRegions,
        territoriesByRegion,
      );

      try {
        const res = await summon.post<IClipSearch, IElasticSearchRes<IClip>>('clips/search', fullReq);
        const data = res.hits.hits.map(hit => hit._source);

        setClips({
          data,
          metadata: {
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            totalCount: res.hits.total,
          },
        });
      } catch (e) {
        setIsError(true);
      } finally {
        setIsFetching(false);
      }
    },
    150,
    {leading: false, trailing: true},
  );

  return {
    clips,
    isError,
    isFetching,
    rowsPerPage,
    setRowsPerPage,
    page,
    setPage,
    sort,
    setSort,
    search,
    clearSearch,
  };
};

interface IuseGetClipVideo {
  cc?: IPlayerSubtitle[];
  isLoading: boolean;
  playerType: 'progressive' | 'hls';
  setActiveClip: (clip?: IClip) => void;
  signedUrl?: string;
  streamUrl?: string;
}

export const useGetClipVideo = (): IuseGetClipVideo => {
  const [clip, setClip] = React.useState<IClip>();
  const [cc, setCC] = React.useState<IPlayerSubtitle[]>();
  const [isLoading, setIsLoading] = React.useState(true);
  const [playerType, setPlayerType] = React.useState<'progressive' | 'hls'>('hls');
  const [streamUrl, setStreamUrl] = React.useState<string>();
  const [signedUrl, setSignedUrl] = React.useState<string>();

  const setActiveClip = (clip?: IClip) => setClip(clip);

  const [summon] = useCancellableSummon();

  const getSignedVideoUrl = async (clipId: string): Promise<string> =>
    (await summon.get<void, {downloadUrl: string}>(`clips/${clipId}/resource/video`)).downloadUrl;
  const isMp4 = (url?: string) => url?.toLowerCase().endsWith('mp4');

  const reset = () => {
    setIsLoading(true);
    setCC(undefined);
    setPlayerType('hls');
    setStreamUrl(undefined);
    setSignedUrl(undefined);
  };

  React.useEffect(() => {
    if (!clip || (!clip._id && !clip.id)) {
      return;
    }

    reset();

    const subtitleData: IPlayerSubtitle[] = [];
    let hasSignedUrl = false;

    // ES and DAL handle the clip ID differently
    const clipId = clip.id || clip._id;

    (async () => {
      /** This is a pretty safe failback */
      if (clip.origin?.type === 'HLS') {
        setPlayerType('hls');
        setStreamUrl(clip.origin.url);
      } else if (clip.origin?.url && isMp4(clip.origin?.url)) {
        setPlayerType('progressive');

        try {
          const streamUrl = await getSignedVideoUrl(clipId);
          setStreamUrl(streamUrl);
          setSignedUrl(streamUrl);
          hasSignedUrl = true;
        } catch (e) {
          setStreamUrl(clip.origin.url);
        }
      }
      /**
       * This is commented out until we can figure out
       * the issue with HLS playback with DRM clips...
       */
      // Clip hasn't been ingested yet so use the origin
      // if (!clip.url) {
      //   if (clip.origin?.type === 'HLS') {
      //     setPlayerType('hls');
      //     setStreamUrl(clip.origin.url);
      //   } else {
      //     setPlayerType('progressive');

      //     try {
      //       const streamUrl = await getSignedVideoUrl(clipId);
      //       setStreamUrl(streamUrl);
      //       setSignedUrl(streamUrl);
      //       hasSignedUrl = true;
      //     } catch (e) {}
      //   }
      // } else {
      //   if (isMp4(clip.url)) {
      //     setPlayerType('progressive');

      //     try {
      //       const streamUrl = await getSignedVideoUrl(clipId);
      //       setSignedUrl(streamUrl);
      //       setStreamUrl(streamUrl);
      //     } catch (e) {
      //       setStreamUrl(clip.url);
      //     }
      //   } else {
      //     setPlayerType('hls');
      //     setStreamUrl(clip.url);
      //   }
      // }

      for (const cc of clip?.cc || []) {
        // ES and DAL handle the cc ID differently
        const ccId = cc.id || cc._id;

        if (ccId) {
          try {
            const ccData = await summon.get<void, {downloadUrl: string}>(`clips/${clipId}/resource/cc/${ccId}`);

            const label =
              cc.language === 'en'
                ? 'English'
                : cc.language === 'es'
                ? 'Spanish'
                : cc.language === 'de'
                ? 'German'
                : cc.language;

            subtitleData.push({
              label,
              enabled: false,
              language: cc.language,
              source: ccData.downloadUrl,
            });
          } catch (e) {}
        }
      }

      if (subtitleData.length) {
        setCC(subtitleData);
      }

      // Get Signed URL
      if (isMp4(clip.origin?.type) && !hasSignedUrl) {
        try {
          const streamUrl = await getSignedVideoUrl(clipId);
          setSignedUrl(streamUrl);
        } catch (e) {}
      }
    })();

    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clip]);

  return {
    cc,
    isLoading,
    playerType,
    setActiveClip,
    signedUrl,
    streamUrl,
  };
};
